import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_BASE = '/machinesim/api';

const ToolManager = ({ machineName, onClose }) => {
  const [tools, setTools] = useState({});
  const [loading, setLoading] = useState(true);
  const [editingTool, setEditingTool] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    type: 'End Mill',
    diameter: '',
    length: '',
    material: 'HSS',
    description: ''
  });

  const toolTypes = [
    'End Mill', 'Ball End Mill', 'Face Mill', 'Drill', 'Tap', 
    'Reamer', 'Boring Bar', 'Chamfer Mill', 'Slot Mill', 'Other'
  ];

  const materials = [
    'HSS', 'Carbide', 'Ceramic', 'CBN', 'Diamond', 'Coated Carbide'
  ];

  // Fetch tools data
  const fetchTools = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE}/${machineName}/tools`);
      if (response.data.success) {
        setTools(response.data.tools);
      }
    } catch (error) {
      console.error('Failed to fetch tools:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTools();
  }, [machineName]);

  const handleEdit = (toolNumber) => {
    const tool = tools[`T${toolNumber}`];
    setFormData({
      name: tool.name || '',
      type: tool.type || 'End Mill',
      diameter: tool.diameter || '',
      length: tool.length || '',
      material: tool.material || 'HSS',
      description: tool.description || ''
    });
    setEditingTool(toolNumber);
  };

  const handleSave = async () => {
    try {
      const response = await axios.post(
        `${API_BASE}/${machineName}/tools/${editingTool}`,
        formData
      );
      
      if (response.data.success) {
        await fetchTools();
        setEditingTool(null);
        setFormData({
          name: '',
          type: 'End Mill',
          diameter: '',
          length: '',
          material: 'HSS',
          description: ''
        });
      }
    } catch (error) {
      console.error('Failed to save tool:', error);
      alert('Failed to save tool: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleDelete = async (toolNumber) => {
    if (!confirm(`Are you sure you want to reset Tool ${toolNumber} to default?`)) {
      return;
    }

    try {
      const response = await axios.delete(`${API_BASE}/${machineName}/tools/${toolNumber}`);
      if (response.data.success) {
        await fetchTools();
      }
    } catch (error) {
      console.error('Failed to delete tool:', error);
      alert('Failed to reset tool: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleCancel = () => {
    setEditingTool(null);
    setFormData({
      name: '',
      type: 'End Mill',
      diameter: '',
      length: '',
      material: 'HSS',
      description: ''
    });
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-gray-800 p-6 rounded-lg">
          <div className="text-white">Loading tools...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-lg border-2 border-gray-600 max-w-6xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-600">
          <h2 className="text-white text-2xl font-bold">Tool Manager - {machineName}</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white text-3xl"
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {[1, 2, 3, 4].map(toolNum => {
              const tool = tools[`T${toolNum}`] || {};
              const isEditing = editingTool === toolNum;
              const percentage = tool.preset > 0 ? Math.round((tool.count / tool.preset) * 100) : 0;
              const isNearLimit = percentage >= 80;
              const isAtLimit = percentage >= 100;

              return (
                <div key={toolNum} className="bg-gray-700 p-4 rounded-lg border border-gray-600">
                  <div className="flex justify-between items-start mb-4">
                    <h3 className="text-green-400 font-mono font-bold text-xl">T{toolNum}</h3>
                    <div className="flex gap-2">
                      {!isEditing && (
                        <>
                          <button
                            onClick={() => handleEdit(toolNum)}
                            className="px-3 py-1 bg-blue-600 hover:bg-blue-700 rounded text-white text-sm"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDelete(toolNum)}
                            className="px-3 py-1 bg-red-600 hover:bg-red-700 rounded text-white text-sm"
                          >
                            Reset
                          </button>
                        </>
                      )}
                    </div>
                  </div>

                  {isEditing ? (
                    <div className="space-y-3">
                      <div>
                        <label className="text-gray-300 text-sm block mb-1">Name:</label>
                        <input
                          type="text"
                          value={formData.name}
                          onChange={(e) => setFormData({...formData, name: e.target.value})}
                          className="w-full bg-gray-600 text-white px-3 py-2 rounded border border-gray-500"
                          placeholder="Tool name"
                        />
                      </div>
                      
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-gray-300 text-sm block mb-1">Type:</label>
                          <select
                            value={formData.type}
                            onChange={(e) => setFormData({...formData, type: e.target.value})}
                            className="w-full bg-gray-600 text-white px-3 py-2 rounded border border-gray-500"
                          >
                            {toolTypes.map(type => (
                              <option key={type} value={type}>{type}</option>
                            ))}
                          </select>
                        </div>
                        
                        <div>
                          <label className="text-gray-300 text-sm block mb-1">Material:</label>
                          <select
                            value={formData.material}
                            onChange={(e) => setFormData({...formData, material: e.target.value})}
                            className="w-full bg-gray-600 text-white px-3 py-2 rounded border border-gray-500"
                          >
                            {materials.map(material => (
                              <option key={material} value={material}>{material}</option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-gray-300 text-sm block mb-1">Diameter (mm):</label>
                          <input
                            type="number"
                            step="0.01"
                            value={formData.diameter}
                            onChange={(e) => setFormData({...formData, diameter: e.target.value})}
                            className="w-full bg-gray-600 text-white px-3 py-2 rounded border border-gray-500"
                            placeholder="0.00"
                          />
                        </div>
                        
                        <div>
                          <label className="text-gray-300 text-sm block mb-1">Length (mm):</label>
                          <input
                            type="number"
                            step="0.01"
                            value={formData.length}
                            onChange={(e) => setFormData({...formData, length: e.target.value})}
                            className="w-full bg-gray-600 text-white px-3 py-2 rounded border border-gray-500"
                            placeholder="0.00"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="text-gray-300 text-sm block mb-1">Description:</label>
                        <textarea
                          value={formData.description}
                          onChange={(e) => setFormData({...formData, description: e.target.value})}
                          className="w-full bg-gray-600 text-white px-3 py-2 rounded border border-gray-500"
                          rows="2"
                          placeholder="Tool description or notes"
                        />
                      </div>

                      <div className="flex gap-2 pt-2">
                        <button
                          onClick={handleSave}
                          className="px-4 py-2 bg-green-600 hover:bg-green-700 rounded text-white font-medium"
                        >
                          Save
                        </button>
                        <button
                          onClick={handleCancel}
                          className="px-4 py-2 bg-gray-600 hover:bg-gray-700 rounded text-white font-medium"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div className="text-white font-medium">{tool.name || `Tool ${toolNum}`}</div>
                      
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div className="text-gray-300">
                          <span className="text-gray-400">Type:</span> {tool.type || 'End Mill'}
                        </div>
                        <div className="text-gray-300">
                          <span className="text-gray-400">Material:</span> {tool.material || 'HSS'}
                        </div>
                        <div className="text-gray-300">
                          <span className="text-gray-400">Diameter:</span> {tool.diameter || 0}mm
                        </div>
                        <div className="text-gray-300">
                          <span className="text-gray-400">Length:</span> {tool.length || 0}mm
                        </div>
                      </div>

                      {tool.description && (
                        <div className="text-gray-300 text-sm">
                          <span className="text-gray-400">Notes:</span> {tool.description}
                        </div>
                      )}

                      {/* Tool Life Status */}
                      <div className="bg-gray-800 p-3 rounded mt-3">
                        <div className="text-gray-300 text-sm mb-1">Tool Life:</div>
                        <div className={`font-mono ${isAtLimit ? 'text-red-400 animate-pulse' : isNearLimit ? 'text-yellow-400' : 'text-green-400'}`}>
                          {tool.count || 0}/{tool.preset || 100} ({percentage}%)
                        </div>
                        <div className="w-full bg-gray-600 rounded-full h-2 mt-2">
                          <div 
                            className={`h-2 rounded-full transition-all ${isAtLimit ? 'bg-red-500' : isNearLimit ? 'bg-yellow-500' : 'bg-green-500'}`}
                            style={{ width: `${Math.min(percentage, 100)}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ToolManager; 